import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { RequiredUserActionsService } from '../required-user-actions.service';
import { RequiredUserActionHolder } from 'src/app/_services/user-action-validator/required-user-action-validator.service';
import { RequiredUserActionsDaoService } from '../../dao/required-user-actions-dao.service';

/**
 * Loads App Parameters
 */
@Injectable()
export class RequiredUserActionsLogicService
  implements RequiredUserActionsService
{
  public requiredUserActions$ = new BehaviorSubject<RequiredUserActionHolder[]>(
    [],
  );
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private authenticationService: AuthenticationService,
    private requiredUserActionsDaoService: RequiredUserActionsDaoService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    await this.requiredUserActionsDaoService.reset();
  }

  public async clear() {
    this.requiredUserActionsDaoService.clear();
    this.requiredUserActions$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteRequiredUserActions(apiLevel, syncKey);
  }
  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalRequiredUserActions();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async loadLocalRequiredUserActions() {
    try {
      this.logger.log(
        this.constructor.name,
        '- Load local RequiredUserActions',
      );

      let actions: RequiredUserActionHolder[] =
        await this.requiredUserActionsDaoService.getRequiredUserActionsData();

      this.logger.log(this.constructor.name, '- RequiredUserActions:', actions);
      this.requiredUserActions$.next(actions);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local RequiredUserActions: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteRequiredUserActions(apiLevel: APILevel, syncKey: string) {
    if (apiLevel < APILevel.REQUIREDUSERACTIONS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load required user actions init with none',
        apiLevel,
        APILevel.REQUIREDUSERACTIONS,
      );
      var actions: RequiredUserActionHolder[] = [];
      await this.requiredUserActionsDaoService.saveRequiredUserActionsData(
        actions,
      );
      await this.loadLocalRequiredUserActions();
      return;
    }

    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new RequiredUserActions from IMS',
      );

      var actions: RequiredUserActionHolder[];
      try {
        let data: any = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'requireduseractions',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          }),
        );

        this.logger.log(this.constructor.name, 'Downloaded Data:', data);

        actions = data;

        this.logger.log(
          this.constructor.name,
          'Downloaded RequiredUserActions: ',
          actions,
        );

        await this.requiredUserActionsDaoService.saveRequiredUserActionsData(actions);
        this.requiredUserActions$.next(actions);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading RequiredUserActions: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  async initializeCompleted(): Promise<void> {}
}
